import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { GenericPlace } from 'src/app/core/models/generic-place.model';
import {
  allowOnlyLettersAndNumbers,
  allowPasteOnlyLettersAndNumbers,
  allowDropOnlyLettersAndNumbers
} from '../../../../shared/functions/input-validations.functions';

declare var google;

@Component({
  selector: 'search-place-component',
  templateUrl: './search-place.component.html',
  styleUrls: ['./search-place.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchPlaceComponent),
      multi: true
    }
  ]
})
export class SearchPlaceComponent implements AfterViewInit, ControlValueAccessor, OnInit {
  private entryEvent: Subject<string> = new Subject();

  private readonly ICON_PIN: string = 'assets/icons/icono-locacion.png';

  readonly DEFAULT_DEBOUNCE_TIME: number = 1000;

  @ViewChild('input', { static: true }) locationInput: ElementRef<HTMLInputElement>;

  //#region GEOLOCATION
  geocoder: any;
  placesCollection: IOptionPlace[] = new Array<IOptionPlace>();
  resultPlace: GenericPlace = new GenericPlace();
  isInvalid: boolean = false;
  //#endregion GEOLOCATION

  @Input() value: string = '';
  @Output() selectedEvent: EventEmitter<GenericPlace> = new EventEmitter();
  @Output() cleanLocationInput: EventEmitter<any> = new EventEmitter();

  private _isPresent: boolean = false;

  propagateChange = (_: any) => {};

  set inputValue(value: string | IOptionPlace) {
    if (value == '') {
      this.isInvalid = true;
    }
    if (typeof value === 'string') {
      this.entryEvent.next(value);
    } else {
      this.isInvalid = false;
      this.placesCollection = new Array<IOptionPlace>();
      this.onSelected(value);
      this.locationInput.nativeElement.value = value.label;
    }
  }

  ngOnInit() {
    this.entryEvent.subscribe(async (value) => {
      if (value) {
        this.placesCollection = await this.runGoogleAutocomplete(value);
      } else {
        this.selectedEvent.emit(null);
        this.resetPlacesCollection();
      }
    });
  }

  cleanLocationInputEvent(buttonPressed: boolean = false): void {
    if (buttonPressed) {
      this.cleanLocationInput.emit(true);
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.geocoder = new google.maps.Geocoder();
    }, 500);
  }

  //#region COMPONENT CONTROLS
  writeValue(place: any): void {
    if (place! instanceof GenericPlace) {
      let current = <IOptionPlace>{};
      current.label = place.nombreDireccion;
      current.value = place.placeId;
      current.latitude = place.latitud.toString();
      current.longitude = place.longitud.toString();
      current.icon = this.ICON_PIN;

      this.placesCollection.push(current);
      this.setValue(place.nombreDireccion);
    } else if (typeof place === 'string') {
      this.locationInput.nativeElement.value = place;
      this.setValue(place);
    } else {
      this.setValue();
    }
  }

  registerOnChange(func: any): void {
    this.propagateChange = func;
  }

  registerOnTouched(): void {}

  setValue(value: string = ''): void {
    this.value = value;
    this._isPresent = value != '';
  }
  //#endregion

  //#region GOOGLE PLACE CONTROLS
  onSelected(event: IOptionPlace): void {
    this.geocoder.geocode({ address: event.label }, (results, status) => {
      if (status == google.maps.GeocoderStatus.OK) {
        let [address] = results;

        const getComponent = (componentsCollection: any[], type: 'locality' | 'postal_code'): string => {
          let response = '';
          for (let component of componentsCollection) {
            if (component.types.includes(type)) {
              response = component.long_name;
            }
          }
          return response;
        };

        this.resultPlace = new GenericPlace();
        this.resultPlace.latitud = address.geometry.location.lat();
        this.resultPlace.longitud = address.geometry.location.lng();
        this.resultPlace.nombreDireccion = event.label;
        this.resultPlace.placeId = event.value;
        this.resultPlace.ciudad = event.city || getComponent(address.address_components, 'locality');
        this.resultPlace.codigoPostal = getComponent(address.address_components, 'postal_code');
        this.resultPlace.provincia = event.province;
        this.resultPlace.pais = event.country;
        this.setValue(event.value);
        this.selectedEvent.emit(this.resultPlace);
      } else {
        this.setValue();
        this.resultPlace = new GenericPlace();
      }
    });
  }

  runGoogleAutocomplete(input: string): Promise<any> {
    return new Promise((resolve) => {
      let autocompleteService = new google.maps.places.AutocompleteService();
      let placeList = new Array<IOptionPlace>();

      let request = <GoogleRequest>{};
      let restriction = <GoogleRestrictions>{};
      restriction.country = 'ar';
      request.componentRestrictions = restriction;
      request.types = ['address'];
      request.input = input;

      autocompleteService.getPlacePredictions(request, (predictionsArr, placesServiceStatus) => {
        if (placesServiceStatus == google.maps.places.PlacesServiceStatus.OK && predictionsArr != undefined) {
          predictionsArr.forEach((element) => {
            let country: string;
            let province: string;
            let city: string = '';
            [country, province, city] = element.structured_formatting.secondary_text.split(',').reverse();
            placeList.push({
              label: element.description,
              value: element.place_id,
              icon: this.ICON_PIN,
              city: city ? city.trim() : null,
              province: province.trim(),
              country: country.trim()
            });
          });
          resolve(placeList);
        }
      });
    });
  }

  resetPlacesCollection(): void {
    this.placesCollection = new Array<IOptionPlace>();
  }
  //#endregion

  //#region PUBLIC METHODS
  public get(): GenericPlace | null {
    if (this.resultPlace.placeId != '') {
      return this.resultPlace;
    } else {
      return null;
    }
  }

  public isPresent(): boolean {
    return this._isPresent;
  }

  public checkPattern(event: KeyboardEvent) {
    allowOnlyLettersAndNumbers(event);
  }

  public checkPaste(event: ClipboardEvent) {
    allowPasteOnlyLettersAndNumbers(event);
  }

  public checkDrop(event: DragEvent) {
    allowDropOnlyLettersAndNumbers(event);
  }

  //#endregion
}

interface IOptionPlace {
  latitude?: string;
  longitude?: string;
  value: string;
  label: string;
  icon?: string;
  city: string;
  province: string;
  country: string;
}

interface GoogleRequest {
  input: string;
  componentRestrictions?: GoogleRestrictions;
  types: string[];
}

interface GoogleRestrictions {
  country: string;
}
